import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table,
} from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { adminApi, extractError } from '../api/client';
import { useToast } from '../contexts/ToastContext';

/**
 * Zip Code Management
 * ----------------------------------------------------------------------------
 * Bulk-import ZIP → state/county mappings from an xlsx, and browse/filter
 * them by state. A ZIP can map to multiple counties; we display them as
 * sibling rows so admins can see the overlap.
 *
 * Companion public endpoints (no auth):
 *   GET /api/v1/zip-codes/{zip}
 *   GET /api/v1/zip-codes/{zip}/page
 */
export default function ZipCodesPage() {
  const toast = useToast();
  const fileRef = useRef(null);

  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [q, setQ] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);

  // Import modal state
  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [autoCreate, setAutoCreate] = useState(true);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Delete-by-state modal
  const [confirmPurge, setConfirmPurge] = useState('');

  async function refresh() {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (q) params.set('q', q);
      if (stateFilter) params.set('state', stateFilter);
      const [listRes, statsRes] = await Promise.all([
        adminApi.get(`/admin/zip-codes?${params.toString()}`),
        adminApi.get('/admin/zip-codes/stats'),
      ]);
      setItems(listRes.data.items || []);
      setTotal(listRes.data.total || 0);
      setStats(statsRes.data);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }

  // Re-fetch whenever filters or page change. We debounce text input.
  useEffect(() => {
    const t = setTimeout(() => { refresh(); }, q ? 250 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, stateFilter, page]);

  async function doImport() {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error('Pick an .xlsx file first.'); return; }
    if (!/\.xlsx$/i.test(file.name)) { toast.error('File must be .xlsx'); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('auto_create_missing', String(autoCreate));
      form.append('replace_existing_for_state', String(replaceExisting));
      const res = await adminApi.post('/admin/zip-codes/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      const summary = `${res.data.inserted} new, ${res.data.updated} updated`;
      toast.success(`Imported: ${summary}`);
      await refresh();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setImporting(false);
    }
  }

  async function purgeState() {
    if (!confirmPurge) return;
    try {
      const res = await adminApi.delete(`/admin/zip-codes?state=${encodeURIComponent(confirmPurge)}`);
      toast.success(`Removed ${res.data.deleted} rows for ${confirmPurge}.`);
      setConfirmPurge('');
      if (stateFilter === confirmPurge) setStateFilter('');
      await refresh();
    } catch (e) {
      toast.error(extractError(e));
    }
  }

  const totalPages = useMemo(() => Math.ceil(total / pageSize) || 1, [total, pageSize]);

  return (
    <>
      <PageHeader
        title="Zip Code Management"
        subtitle="Import ZIP → state/county mappings from an xlsx. A single ZIP can overlap multiple counties; we store one row per overlap."
        actions={
          <Button variant="primary" onClick={() => { setShowImport(true); setImportResult(null); }} data-testid="zip-import-open-button">
            <i className="bi bi-upload me-1" /> Import xlsx
          </Button>
        }
      />

      <div className="page-content">
        {error && <Alert variant="danger">{error}</Alert>}

        {/* Stats row */}
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-3">
              <Col xs={6} md={3}>
                <div className="text-muted" style={{ fontSize: '0.78rem' }}>Total rows</div>
                <div style={{ fontSize: '1.6rem', fontWeight: 600 }} data-testid="zip-stat-total">{stats?.total ?? '—'}</div>
              </Col>
              <Col xs={6} md={3}>
                <div className="text-muted" style={{ fontSize: '0.78rem' }}>States covered</div>
                <div style={{ fontSize: '1.6rem', fontWeight: 600 }} data-testid="zip-stat-states">{stats?.states_count ?? '—'}</div>
              </Col>
              <Col xs={12} md={6}>
                <div className="text-muted mb-1" style={{ fontSize: '0.78rem' }}>By state</div>
                <div className="d-flex flex-wrap gap-1">
                  {(stats?.states || []).map((s) => (
                    <Badge
                      key={s.state_abbreviation}
                      bg={stateFilter === s.state_abbreviation ? 'primary' : 'light'}
                      text={stateFilter === s.state_abbreviation ? undefined : 'dark'}
                      style={{ cursor: 'pointer' }}
                      onClick={() => { setStateFilter(stateFilter === s.state_abbreviation ? '' : s.state_abbreviation); setPage(1); }}
                      data-testid={`zip-state-badge-${s.state_abbreviation}`}
                    >
                      {s.state_abbreviation} · {s.unique_zips} zips ({s.count})
                    </Badge>
                  ))}
                  {(!stats?.states || stats.states.length === 0) && (
                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>No data imported yet.</span>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Filter bar */}
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-2 align-items-end">
              <Col xs={12} md={5}>
                <Form.Label className="text-muted" style={{ fontSize: '0.78rem' }}>Search</Form.Label>
                <Form.Control
                  placeholder="ZIP prefix (e.g. 330), city, or county name"
                  value={q}
                  onChange={(e) => { setQ(e.target.value); setPage(1); }}
                  data-testid="zip-search-input"
                />
              </Col>
              <Col xs={6} md={3}>
                <Form.Label className="text-muted" style={{ fontSize: '0.78rem' }}>State</Form.Label>
                <Form.Select
                  value={stateFilter}
                  onChange={(e) => { setStateFilter(e.target.value); setPage(1); }}
                  data-testid="zip-state-filter-select"
                >
                  <option value="">All states</option>
                  {(stats?.states || []).map((s) => (
                    <option key={s.state_abbreviation} value={s.state_abbreviation}>
                      {s.state_abbreviation} ({s.unique_zips})
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={6} md={4} className="d-flex gap-2 justify-content-end">
                {stateFilter && (
                  <Button variant="outline-danger" size="sm" onClick={() => setConfirmPurge(stateFilter)} data-testid="zip-purge-state-button">
                    <i className="bi bi-trash me-1" /> Delete all {stateFilter}
                  </Button>
                )}
                <Button variant="outline-secondary" size="sm" onClick={refresh} disabled={loading}>
                  <i className="bi bi-arrow-clockwise me-1" /> Refresh
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* List */}
        <Card data-testid="zip-codes-card">
          {loading ? (
            <div className="text-center p-5"><Spinner animation="border" /></div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-mailbox" />
              <div>No ZIP codes match your filters.</div>
              <Button variant="primary" className="mt-2" onClick={() => setShowImport(true)}>
                Import xlsx
              </Button>
            </div>
          ) : (
            <Table hover responsive size="sm" className="mb-0" data-testid="zip-codes-table">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>ZIP</th>
                  <th style={{ width: 70 }}>State</th>
                  <th>City</th>
                  <th>County</th>
                  <th style={{ width: 110 }}>State FIPS</th>
                  <th style={{ width: 110 }}>County FIPS</th>
                  <th>Linked</th>
                </tr>
              </thead>
              <tbody>
                {items.map((z) => (
                  <tr key={z.id} data-testid={`zip-row-${z.id}`}>
                    <td><code>{z.zip}</code></td>
                    <td>{z.state_abbreviation}</td>
                    <td style={{ fontSize: '0.88rem' }} data-testid={`zip-city-${z.id}`}>
                      {z.city || <span className="text-muted">—</span>}
                    </td>
                    <td>{z.county_name}</td>
                    <td><code className="text-muted">{z.state_fips}</code></td>
                    <td><code className="text-muted">{z.county_fips}</code></td>
                    <td style={{ fontSize: '0.78rem' }}>
                      {z.state_id ? (
                        <Badge bg="success">state ✓</Badge>
                      ) : (
                        <Badge bg="warning" text="dark">state ?</Badge>
                      )}{' '}
                      {z.county_id ? (
                        <Badge bg="success">county ✓</Badge>
                      ) : (
                        <Badge bg="warning" text="dark">county ?</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-2" style={{ fontSize: '0.85rem' }}>
            <span className="text-muted">Page {page} of {totalPages} · {total} rows</span>
            <div className="d-flex gap-1">
              <Button size="sm" variant="outline-secondary" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</Button>
              <Button size="sm" variant="outline-secondary" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</Button>
            </div>
          </div>
        )}
      </div>

      {/* Import modal */}
      <Modal show={showImport} onHide={() => !importing && setShowImport(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>Import ZIP codes</Modal.Title></Modal.Header>
        <Modal.Body>
          <Alert variant="info" style={{ fontSize: '0.85rem' }}>
            <strong>Expected columns</strong> (header row, in any order — extra columns are ignored):<br />
            <code>ZIP</code>, <code>USPS Default State for ZIP</code>, <code>USPS Default City for ZIP</code> <span className="text-muted">(optional)</span>, <code>County</code>, <code>County State</code>, <code>State Abbreviation</code>, <code>County FIPS code</code>, <code>State FIPS code</code>
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label>xlsx file</Form.Label>
            <Form.Control type="file" accept=".xlsx" ref={fileRef} disabled={importing} data-testid="zip-import-file-input" />
          </Form.Group>

          <Form.Check
            type="switch"
            id="auto-create-toggle"
            label={<><strong>Auto-create missing states and counties from FIPS data</strong> <span className="text-muted">— recommended when importing a new state for the first time.</span></>}
            checked={autoCreate}
            onChange={(e) => setAutoCreate(e.target.checked)}
            disabled={importing}
            data-testid="zip-import-auto-create-toggle"
          />
          <Form.Check
            type="switch"
            id="replace-existing-toggle"
            className="mt-2"
            label={<>Replace existing rows for every state in this file <span className="text-muted">(delete-then-insert per state)</span></>}
            checked={replaceExisting}
            onChange={(e) => setReplaceExisting(e.target.checked)}
            disabled={importing}
            data-testid="zip-import-replace-existing-toggle"
          />

          {importResult && (
            <Card className="mt-3">
              <Card.Body style={{ fontSize: '0.85rem' }}>
                <div className="d-flex flex-wrap gap-2 mb-2">
                  <Badge bg="success">{importResult.inserted} inserted</Badge>
                  <Badge bg="primary">{importResult.updated} updated</Badge>
                  {importResult.skipped > 0 && <Badge bg="warning" text="dark">{importResult.skipped} skipped</Badge>}
                  {importResult.auto_created_states > 0 && <Badge bg="info">+{importResult.auto_created_states} states</Badge>}
                  {importResult.auto_created_counties > 0 && <Badge bg="info">+{importResult.auto_created_counties} counties</Badge>}
                </div>
                {(importResult.unmatched_states || []).length > 0 && (
                  <Alert variant="warning" style={{ fontSize: '0.8rem' }}>
                    <strong>Unmatched states:</strong> {importResult.unmatched_states.join(', ')}.<br />
                    Re-run with "Auto-create missing" enabled to add them.
                  </Alert>
                )}
                {(importResult.unmatched_counties || []).length > 0 && (
                  <Alert variant="warning" style={{ fontSize: '0.8rem' }}>
                    <strong>Unmatched counties</strong> ({importResult.unmatched_counties.length} shown): {importResult.unmatched_counties.slice(0, 10).join(', ')}{importResult.unmatched_counties.length > 10 ? ', …' : ''}
                  </Alert>
                )}
              </Card.Body>
            </Card>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImport(false)} disabled={importing}>Close</Button>
          <Button variant="primary" onClick={doImport} disabled={importing} data-testid="zip-import-submit-button">
            {importing ? <><Spinner size="sm" animation="border" /> Importing…</> : <><i className="bi bi-upload me-1" /> Import</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Purge confirm */}
      <Modal show={!!confirmPurge} onHide={() => setConfirmPurge('')}>
        <Modal.Header closeButton><Modal.Title>Delete ZIP rows for {confirmPurge}?</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>This will remove every ZIP→county row whose state is <strong>{confirmPurge}</strong>. The state and county records themselves are NOT touched.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmPurge('')}>Cancel</Button>
          <Button variant="danger" onClick={purgeState} data-testid="zip-purge-confirm-button">Delete rows</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
